import { useState } from "react";
import { Alert, Button, Card, Checkbox, DatePicker, InputNumber, Progress, Space, Typography, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import { HabitCompletionStatus } from "@fitness-app/data-models/entities/Habit";
import { MeasurementType } from "@fitness-app/data-models/entities/Measurement";

import { type SimulatorTarget } from "./useSimulatorTarget";
import {
  DIRECTLY_COMPLETABLE_TASK_TYPES,
  listTraineeChallenges,
  toggleChallengeTaskCompletion,
} from "./writes/challengeWrites";
import { listTraineeHabits, toggleHabitCompletion } from "./writes/habitWrites";
import { writeMeasurement } from "./writes/measurementWrites";
import { allDishKeys, resolveNutritionDay, trackNutritionDay } from "./writes/nutritionWrites";
import { createCompletedWorkoutActivity, fetchProgramWorkouts } from "./writes/workoutWrites";

type Props = {
  target: SimulatorTarget;
  createdBy: string;
  onWritten: () => void;
};

type Scope = {
  workouts: boolean;
  nutrition: boolean;
  habits: boolean;
  challengeTasks: boolean;
  weight: boolean;
};

const DEFAULT_SCOPE: Scope = {
  workouts: true,
  nutrition: true,
  habits: true,
  challengeTasks: true,
  weight: true,
};

const SimulatorBulkGenerator = ({ target, createdBy, onWritten }: Props) => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(13, "day"), dayjs()]);
  const [scope, setScope] = useState<Scope>(DEFAULT_SCOPE);
  const [workoutsPerWeek, setWorkoutsPerWeek] = useState(3);
  const [startWeight, setStartWeight] = useState(65);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const run = async () => {
    const [start, end] = range;
    const days = Math.max(0, end.startOf("day").diff(start.startOf("day"), "day")) + 1;

    setProgress({ done: 0, total: days });
    setErrors([]);

    const program =
      scope.workouts && target.activeTrainingProgramId
        ? await fetchProgramWorkouts(target.activeTrainingProgramId)
        : { workouts: [], startDate: null };

    // Rozstaw treningów w tygodniu: przy 3 na tydzień wypada co drugi dzień z okładem.
    const workoutGap = workoutsPerWeek > 0 ? Math.max(1, Math.round(7 / workoutsPerWeek)) : 0;
    const failures: string[] = [];

    for (let offset = 0; offset < days; offset += 1) {
      const day = start.add(offset, "day");
      const date = day.format("YYYY-MM-DD");

      try {
        if (scope.habits) {
          const habits = await listTraineeHabits(target.id, date);
          for (const habit of habits) {
            if (habit.dayStatus !== HabitCompletionStatus.Completed) {
              await toggleHabitCompletion({
                habit,
                traineeId: target.id,
                date,
                createdBy,
                completed: true,
                mode: "bulk",
              });
            }
          }
        }

        if (scope.challengeTasks) {
          const challenges = await listTraineeChallenges(target.id, date);
          for (const challenge of challenges) {
            // Poza oknem wyzwania odhaczenie i tak nie punktuje — nie zaśmiecaj bazy.
            if (day.isBefore(challenge.startDate, "day") || day.isAfter(challenge.endDate, "day")) {
              continue;
            }
            for (const task of challenge.tasks) {
              if (DIRECTLY_COMPLETABLE_TASK_TYPES.includes(task.type) && !task.completionId) {
                await toggleChallengeTaskCompletion({
                  challengeId: challenge.challengeId,
                  participantId: challenge.participantId,
                  task,
                  traineeId: target.id,
                  date,
                  createdBy,
                  completed: true,
                  mode: "bulk",
                });
              }
            }
          }
        }

        if (scope.nutrition) {
          const nutritionDay = await resolveNutritionDay(target.id, target.activeMealsPlanId, date);
          if (nutritionDay) {
            await trackNutritionDay({
              nutritionDay,
              traineeId: target.id,
              userId: target.userId,
              createdBy,
              date,
              eatenKeys: allDishKeys(nutritionDay.day),
              mode: "bulk",
            });
          }
        }

        if (scope.workouts && workoutGap > 0 && program.workouts.length > 0 && offset % workoutGap === 0) {
          const workout = program.workouts[(offset / workoutGap) % program.workouts.length]!;
          await createCompletedWorkoutActivity({
            workout,
            programId: target.activeTrainingProgramId,
            traineeId: target.id,
            userId: target.userId,
            clientEmail: target.email,
            date,
            createdBy,
            mode: "bulk",
          });
        }

        // Waga raz w tygodniu, w poniedziałki — jak u klientki, która waży się cyklicznie.
        if (scope.weight && day.day() === 1) {
          const weeksIn = Math.floor(offset / 7);
          await writeMeasurement({
            traineeId: target.id,
            userId: target.userId,
            createdBy,
            date,
            mode: "bulk",
            type: MeasurementType.WeightMeasurement,
            data: { weight: Number((startWeight - weeksIn * 0.4).toFixed(1)) },
          });
        }
      } catch (error) {
        failures.push(`${date}: ${error instanceof Error ? error.message : "nieznany błąd"}`);
      }

      setProgress({ done: offset + 1, total: days });
    }

    setErrors(failures);
    onWritten();

    if (failures.length === 0) {
      void message.success(`Wygenerowano ${days} dni aktywności`);
    } else {
      void message.warning(`Zakończono z ${failures.length} błędami`);
    }
  };

  const isRunning = progress !== null && progress.done < progress.total;

  return (
    <Card size="small" title="Generator zakresu dni">
      <Space direction="vertical" size={12} className="w-full">
        <Alert
          type="warning"
          showIcon
          message="Każdy dzień to kilka zapisów, a każdy zapis odpala webhooka"
          description="Przy dłuższym zakresie punkty i ranking dojdą z opóźnieniem — przelicz ranking ręcznie, gdy generator skończy."
        />

        <Space wrap>
          <DatePicker.RangePicker
            value={range}
            allowClear={false}
            disabled={isRunning}
            onChange={(value) => {
              if (value?.[0] && value[1]) {
                setRange([value[0], value[1]]);
              }
            }}
          />
        </Space>

        <Space direction="vertical" size={4}>
          <Checkbox
            checked={scope.habits}
            disabled={isRunning}
            onChange={(event) => setScope((current) => ({ ...current, habits: event.target.checked }))}
          >
            Odhacz nawyki
          </Checkbox>
          <Checkbox
            checked={scope.challengeTasks}
            disabled={isRunning}
            onChange={(event) => setScope((current) => ({ ...current, challengeTasks: event.target.checked }))}
          >
            Odhacz zadania wyzwań (tylko zgłaszalne bezpośrednio)
          </Checkbox>
          <Checkbox
            checked={scope.nutrition}
            disabled={isRunning}
            onChange={(event) => setScope((current) => ({ ...current, nutrition: event.target.checked }))}
          >
            Odhacz posiłki
          </Checkbox>
          <Checkbox
            checked={scope.workouts}
            disabled={isRunning}
            onChange={(event) => setScope((current) => ({ ...current, workouts: event.target.checked }))}
          >
            Zapisz treningi z planu
          </Checkbox>
          <Checkbox
            checked={scope.weight}
            disabled={isRunning}
            onChange={(event) => setScope((current) => ({ ...current, weight: event.target.checked }))}
          >
            Zapisz wagę (w poniedziałki)
          </Checkbox>
        </Space>

        <Space wrap>
          <Space size={4}>
            <Typography.Text type="secondary">Treningów w tygodniu:</Typography.Text>
            <InputNumber
              min={0}
              max={7}
              value={workoutsPerWeek}
              disabled={isRunning || !scope.workouts}
              onChange={(value) => setWorkoutsPerWeek(value ?? 0)}
            />
          </Space>
          <Space size={4}>
            <Typography.Text type="secondary">Waga startowa:</Typography.Text>
            <InputNumber
              min={20}
              max={300}
              step={0.5}
              addonAfter="kg"
              value={startWeight}
              disabled={isRunning || !scope.weight}
              onChange={(value) => setStartWeight(value ?? 0)}
            />
          </Space>
        </Space>

        <Button type="primary" loading={isRunning} onClick={() => void run()}>
          Generuj
        </Button>

        {progress ? (
          <Progress
            percent={Math.round((progress.done / progress.total) * 100)}
            status={isRunning ? "active" : errors.length ? "exception" : "success"}
          />
        ) : null}

        {errors.length ? (
          <Alert
            type="error"
            showIcon
            message={`Błędy w ${errors.length} dniach`}
            description={
              <Space direction="vertical" size={0}>
                {errors.slice(0, 5).map((error) => (
                  <Typography.Text key={error}>{error}</Typography.Text>
                ))}
                {errors.length > 5 ? (
                  <Typography.Text type="secondary">…i {errors.length - 5} więcej</Typography.Text>
                ) : null}
              </Space>
            }
          />
        ) : null}
      </Space>
    </Card>
  );
};

export default SimulatorBulkGenerator;
